import { Component, Input, isDevMode, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { Actions, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { AppState } from 'src/app/core/app.state';
import { IProductHandlingUnit, IProduct, IProductInventoryDetails, IProductVariantInventory, IWarehouse } from '../../../models/products.models';
import { Observable, Subscription, tap } from 'rxjs';
import { SetCurrentElementId } from 'src/app/shared/store/shared.actions';
import { ClearProductInventoryDetails, GetProductInventoryDetails, GetProductInventoryDetailsError, GetProductInventoryDetailsSuccess } from '../../../store/produits.actions';
import { MatDialog } from '@angular/material/dialog';
import { DialogData, HandlingUnitsDialogueComponent } from '../../handling-units-dialogue/handling-units-dialogue.component';
import { ProductsService } from '../../../services/produits.services';
import { FormControl } from '@angular/forms';
import { selectCanDo } from 'src/app/core/store/permissions/permissions.selectors';
export interface IStockDetails {
  total_on_hand: number;
  total_available_stock: number;
  total_reserved_stock: number;
}
@Component({
  selector: 'app-inventory',
  templateUrl: './inventory.component.html',
  styleUrls: ['./inventory.component.scss']
})
export class InventoryComponent implements OnInit {

  @Input() product!: IProduct;  produitId!: number;
  private subscriptions: Subscription[] = [];

  defaultHandlingUnit: number | null | undefined = null;
  defaultWarehouse: number | null | undefined = null;
  productInventoryDetails$: Observable<IProductInventoryDetails | null> = this.store.select((state)=> state.products.productInventoryDetails);
  inventoryDetailsData: IProductInventoryDetails | null = null;
  variants: IProductVariantInventory[] = [];
  variantControl = new FormControl();
  currentHandlingUnits: IProductHandlingUnit[] = [];

  currentWarehouses: IWarehouse[] = [];
  currentStockDetails: IStockDetails = {
    total_on_hand: 0,
    total_available_stock: 0,
    total_reserved_stock: 0
  };

  canEditProduct$ = this.store.select(selectCanDo('product', 'edit'));
  
  constructor(
    private store: Store<AppState>,
    private actions$: Actions,
    private route: ActivatedRoute,
    private router: Router,
    private dialog: MatDialog,
    private productsService: ProductsService,
  ) { }

  ngOnInit(): void {
    this.produitId = +this.route.snapshot.paramMap.get('id')!;
    if (this.produitId) {
      this.store.dispatch(
        SetCurrentElementId({ currentElementId: this.produitId })
      );
      this.store.dispatch(GetProductInventoryDetails({ id: this.produitId }));
    }
    const sub1 = this.productInventoryDetails$.subscribe((inventoryDetails) => {
      if (inventoryDetails) {
        const responseData = inventoryDetails;
          if (responseData.variants && responseData.variants.length > 0) {
            this.variantControl.setValue(responseData.variants[0].product_id);
          }
          const transformHandlingUnits = (units: any[]): IProductHandlingUnit[] => {
            const toFiniteNumber = (v: unknown): number | null => {
              if (v == null || v === false || v === '') {
                return null;
              }
              const n = Number(v);
              return Number.isFinite(n) ? n : null;
            };
            return units.map((unit: any) => {
              const mult = toFiniteNumber(unit.multiplier_from_base_unit);
              const legacyQty = toFiniteNumber(unit.quantity);
              const quantity =
                mult != null && mult > 0
                  ? mult
                  : (legacyQty != null && legacyQty > 0 ? legacyQty : 1);

              const uid = toFiniteNumber(unit.id);
              const tid = toFiniteNumber(unit.type_id);

              const dimRaw = unit.dimenssion_string;
              const dimenssion_string =
                dimRaw === undefined || dimRaw === null || dimRaw === false
                  ? undefined
                  : String(dimRaw);

              return {
                id: uid != null && uid > 0 ? uid : 0,
                type_id: tid != null && tid > 0 ? tid : 0,
                name: unit.name,
                quantity,
                default: unit.default !== undefined ? unit.default : false,
                selected: unit.selected || false,
                dimenssion_string,
                is_base_unit: !!unit.is_base_unit,
                is_main_selling_unit: !!unit.is_main_selling_unit,
                is_allowed_to_erp: !!unit.is_allowed_to_erp,
                is_continuous: !!unit.is_continuous,
                multiplier_from_base_unit: mult,
                rounding_mode: unit.rounding_mode != null && unit.rounding_mode !== false
                  ? String(unit.rounding_mode)
                  : null,
                rounding_decimals: toFiniteNumber(unit.rounding_decimals),
                qty_min: toFiniteNumber(unit.qty_min),
                qty_max: toFiniteNumber(unit.qty_max),
                qty_increment: toFiniteNumber(unit.qty_increment),
                erp_managed: !!unit.erp_managed,
              };
            });
          };
          
          const transformedVariants = (responseData.variants || []).map((variant: any) => ({
            product_id: variant.product_id,
            values: variant.values,
            handling_units: transformHandlingUnits(variant.handling_units || []),
            total_on_hand: variant.total_on_hand,
            total_available_stock: variant.total_available_stock,
            total_reserved_stock: variant.total_reserved_stock,
            warehouses: variant.warehouses
          }));
          
          this.inventoryDetailsData = {
            product_id: responseData.product_id,
            handling_units: transformHandlingUnits(responseData.handling_units || []),
            variants: transformedVariants,
            total_on_hand: responseData.total_on_hand,
            total_available_stock: responseData.total_available_stock,
            total_reserved_stock: responseData.total_reserved_stock,
            warehouses: responseData.warehouses
          };          
          this.variants = transformedVariants;
          
          const currentVariantSelection = this.variantControl.value;
          
          if (currentVariantSelection && currentVariantSelection !== 'all_variants') {
            const variantId = typeof currentVariantSelection === 'string' ? parseInt(currentVariantSelection, 10) : currentVariantSelection;
            const selectedVariant = this.variants.find(v => v.product_id === variantId);
            this.currentHandlingUnits = selectedVariant?.handling_units || [];
            this.currentWarehouses = selectedVariant?.warehouses || [];
            this.currentStockDetails = {
              total_on_hand: selectedVariant?.total_on_hand || 0,
              total_available_stock: selectedVariant?.total_available_stock || 0,
              total_reserved_stock: selectedVariant?.total_reserved_stock || 0
            };
          } else {
            this.currentStockDetails = {
              total_on_hand: this.inventoryDetailsData?.total_on_hand || 0,
              total_available_stock: this.inventoryDetailsData?.total_available_stock || 0,
              total_reserved_stock: this.inventoryDetailsData?.total_reserved_stock || 0
            };
            this.currentHandlingUnits = this.inventoryDetailsData.handling_units;
            this.currentWarehouses = this.inventoryDetailsData.warehouses;
          }
          
          if (this.currentHandlingUnits.length > 0) {
            const defaultUnit = this.currentHandlingUnits.find(hu => hu.default === true);
            this.defaultHandlingUnit = defaultUnit?.id || this.currentHandlingUnits[0].id;
          } else {
            this.defaultHandlingUnit = null;
          }
          if (this.currentWarehouses && this.currentWarehouses.length > 0) {
            const defaultWarehouse = this.currentWarehouses.find(w => w.is_primary === true);
            this.defaultWarehouse = defaultWarehouse?.warehouse_id || this.currentWarehouses[0].warehouse_id;
          } else {
            this.defaultWarehouse = null;
          }
        }
      });

      const sub3 = this.variantControl.valueChanges.subscribe((value) => {
        if (value === 'all_variants') {
          this.currentHandlingUnits = this.inventoryDetailsData?.handling_units || [];
          this.currentWarehouses = this.inventoryDetailsData?.warehouses || [];
          this.currentStockDetails = {
            total_on_hand: this.inventoryDetailsData?.total_on_hand || 0,
            total_available_stock: this.inventoryDetailsData?.total_available_stock || 0,
            total_reserved_stock: this.inventoryDetailsData?.total_reserved_stock || 0
          };
        } else {
          const variantId = typeof value === 'string' ? parseInt(value, 10) : value;
          const variant = this.variants.find(v => v.product_id === variantId);
          this.currentHandlingUnits = variant?.handling_units || [];
          this.currentWarehouses = variant?.warehouses || [];
          this.currentStockDetails = {
            total_on_hand: variant?.total_on_hand || 0,
            total_available_stock: variant?.total_available_stock || 0,
            total_reserved_stock: variant?.total_reserved_stock || 0
          };
        }
        
        if (this.currentHandlingUnits.length > 0) {
          const defaultUnit = this.currentHandlingUnits.find(hu => hu.default === true);
          this.defaultHandlingUnit = defaultUnit?.id || this.currentHandlingUnits[0].id;
        } else {
          this.defaultHandlingUnit = null;
        }
        if (this.currentWarehouses && this.currentWarehouses.length > 0) {
          const defaultWarehouse = this.currentWarehouses.find(w => w.is_primary === true);
          this.defaultWarehouse = defaultWarehouse?.warehouse_id || this.currentWarehouses[0].warehouse_id;
        } else {
          this.defaultWarehouse = null;
        }
      });

      this.subscriptions.push(sub1, sub3)
  }

  cancel(keepParams = true) {
    this.router.navigate(['inventory'], {
      queryParamsHandling: keepParams ? 'merge': ''
    });
  }

  private sortHandlingUnits(units: IProductHandlingUnit[]): IProductHandlingUnit[] {
    return [...units].sort((a, b) => (a.quantity || 0) - (b.quantity || 0));
  }

  getHandlingUnits(): IProductHandlingUnit[] {
    return this.sortHandlingUnits(this.currentHandlingUnits);
  }

  getErpHandlingUnits(): IProductHandlingUnit[] {
    return this.sortHandlingUnits(this.currentHandlingUnits.filter((u) => u.erp_managed));
  }

  getPlatformHandlingUnits(): IProductHandlingUnit[] {
    return this.sortHandlingUnits(this.currentHandlingUnits.filter((u) => !u.erp_managed));
  }

  allowHandlingUnitEdits(): boolean {
    return !!this.product && !(this.variants.length > 1 && this.variantControl.value === 'all_variants');
  }

  getWarehouses() {
    return this.currentWarehouses;
  }

  private getProductVariantIdForHandlingUnits(): number | undefined {
    const selectedValue = this.variantControl.value;
    if (selectedValue && selectedValue !== 'all_variants') {
      return typeof selectedValue === 'string' ? parseInt(selectedValue, 10) : selectedValue;
    }
    if (this.inventoryDetailsData?.product_id && typeof this.inventoryDetailsData.product_id === 'number') {
      return this.inventoryDetailsData.product_id;
    }
    return undefined;
  }

  private openHandlingUnitsDialog(data: DialogData): void {
    const dialogRef = this.dialog.open(HandlingUnitsDialogueComponent, {
      data,
      disableClose: true,
    });
    dialogRef.afterClosed().subscribe((result) => {
      if (result) {
        this.store.dispatch(GetProductInventoryDetails({ id: this.produitId }));
      }
    });
  }

  addHandlingUnits(): void {
    const productVariantId = this.getProductVariantIdForHandlingUnits();
    if (!productVariantId) {
      if (isDevMode()) {
        console.error('Unable to determine product variant ID for handling units');
      }
      return;
    }
    this.openHandlingUnitsDialog({ productVariantId, existingUnits: this.currentHandlingUnits });
  }

  editHandlingUnit(unit: IProductHandlingUnit): void {
    if (unit.erp_managed) {
      return;
    }
    const productVariantId = this.getProductVariantIdForHandlingUnits();
    if (!productVariantId) {
      if (isDevMode()) {
        console.error('Unable to determine product variant ID for handling units');
      }
      return;
    }
    this.openHandlingUnitsDialog({ productVariantId, editUnit: unit });
  }

  onDefaultHandlingUnitChange(value: number): void {
    if (!value) return;
    
    this.defaultHandlingUnit = value;
    this.productsService.setDefaultHandlingUnit(value).subscribe({
      next: () => {
        this.store.dispatch(GetProductInventoryDetails({ id: this.produitId }));
      },
      error: (error) => {
        if (isDevMode()) {
          console.error('Error setting default handling unit:', error);
        }
      }
    });
  }

  getHandlingUnitLabel(handlingUnitId: number | null | undefined): string {
    const handlingUnits = this.getHandlingUnits();
    if (!handlingUnitId || !handlingUnits) {
      return '';
    }
    const handlingUnit = handlingUnits.find(hu => hu.id === handlingUnitId);
    return handlingUnit ? handlingUnit.name : '';
  }
  getWarehouseLabel(warehouseId: number | null | undefined): string {
    const warehouses = this.getWarehouses();
    if (!warehouseId || !warehouses) {
      return '';
    }
    const warehouse = warehouses.find(w => w.warehouse_id === warehouseId);
    return warehouse ? warehouse.name : '';
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach((sub) => sub.unsubscribe());
    this.store.dispatch(ClearProductInventoryDetails());
  }

}
